import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FadeIn, StaggerContainer, StaggerItem, RevealText, SlideReveal } from '../components/AnimatedSection';
import { FloatingHexagons, HexShape, HexDivider } from '../components/HexagonLogo';
import { ParallaxImage, RevealImage, WordReveal, AnimatedCounter, HoverImage, MagneticButton } from '../components/ScrollEffects';
import { HiArrowRight } from 'react-icons/hi';

const results = [
  { num: '397.9K', label: 'Reach in 1 Month', color: '#3E7562' },
  { num: '1,729', label: 'New Followers', color: '#549E84' },
  { num: '150+', label: 'Saves on 3 Reels', color: '#3E7562' },
  { num: '50', label: 'Direct Enquiries', color: '#549E84' },
  { num: '20', label: 'AI Videos Produced', color: '#3E7562' },
  { num: '1', label: 'Week — Website Delivery', color: '#549E84' },
];

const deliverables = [
  { num: '20', label: 'AI Videos', desc: 'Product demos, company profile, social reels', image: 'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=600&q=80' },
  { num: '50', label: 'Content Assets', desc: 'Brochures, flyers, catalogues, social posts', image: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=600&q=80' },
  { num: '4', label: 'Research Reports', desc: 'Competitors, segments, geography, pain points', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80' },
  { num: '1', label: 'Live Website', desc: 'Full design & development in 1 week', image: 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=600&q=80' },
];

export default function CaseStudy() {
  return (
    <div className="page-wrapper">
      {/* Hero with parallax bg */}
      <section style={styles.heroSection}>
        <div style={styles.heroBg}>
          <motion.img
            src="https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=1920&q=80"
            alt="Manufacturing facility"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
          />
          <div style={styles.heroOverlay} />
        </div>
        <FloatingHexagons />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <FadeIn>
            <div className="section-num">01 &mdash; CASE STUDY</div>
          </FadeIn>
          <RevealText>
            <h1 className="section-title" style={{ fontSize: 'clamp(2.8rem, 6vw, 4.5rem)', maxWidth: 700 }}>
              From factory floor to<br /><span className="gradient-text">digital dominance.</span>
            </h1>
          </RevealText>

          <FadeIn delay={0.3}>
            <div style={styles.clientCard}>
              <div>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 8 }}>A Leading Electrical Manufacturer, Telangana</h3>
                <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: 16 }}>
                  Distribution transformers &middot; Control panels &middot; CTPT equipment
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {['AI Video', 'Market Research', 'Content', 'Web Design'].map((s, i) => (
                    <span key={i} style={styles.clientTag}>{s}</span>
                  ))}
                </div>
              </div>
              <div style={styles.badge}>
                <span style={{ fontSize: '0.7rem', color: '#777' }}>Results achieved</span>
                <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#3E7562' }}>within 2 months</span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Before & After Gallery */}
      <section className="section" style={{ background: '#0B0B0B', paddingBottom: 40 }}>
        <div className="container">
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <div className="section-num" style={{ justifyContent: 'center' }}>02 &mdash; THE WORK</div>
              <h2 className="section-title">Transformation <span className="gradient-text">in action.</span></h2>
            </div>
          </FadeIn>
          <div className="grid-2" style={{ gap: 24 }}>
            <SlideReveal direction="left">
              <HoverImage
                src="https://images.unsplash.com/photo-1565043666747-69f6646db940?w=800&q=80"
                alt="Factory production"
                overlay="Factory Floor — Before Digital"
                style={{ width: '100%', height: 350 }}
              />
            </SlideReveal>
            <SlideReveal direction="right" delay={0.15}>
              <HoverImage
                src="https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&q=80"
                alt="Digital presence"
                overlay="Digital Presence — After HTV"
                style={{ width: '100%', height: 350 }}
              />
            </SlideReveal>
          </div>
        </div>
      </section>

      {/* Parallax Break */}
      <ParallaxImage
        src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1920&q=80"
        alt="Industrial excellence"
        height={350}
        speed={0.25}
      />

      {/* Results with Animated Counters */}
      <section className="section" style={{ background: '#111112' }}>
        <div className="container">
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 64 }}>
              <div className="section-num" style={{ justifyContent: 'center' }}>04 &mdash; RESULTS</div>
            </div>
          </FadeIn>
          <WordReveal
            text="Numbers that speak."
            className="section-title"
            style={{ fontSize: 'clamp(2rem, 4.5vw, 3rem)', fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1.15, display: 'block', textAlign: 'center', marginBottom: 56 }}
          />

          <StaggerContainer className="grid-3" staggerDelay={0.1}>
            {results.map((r, i) => (
              <StaggerItem key={i}>
                <motion.div style={styles.resultCard} whileHover={{ y: -6, borderColor: 'rgba(62,117,98,0.2)' }}>
                  <AnimatedCounter value={r.num} suffix="" duration={2.5} />
                  <p style={styles.resultLabel}>{r.label}</p>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Deliverables with images */}
      <section className="section" style={{ background: '#0B0B0B' }}>
        <div className="container">
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 64 }}>
              <div className="section-num" style={{ justifyContent: 'center' }}>06 &mdash; DELIVERABLES</div>
            </div>
          </FadeIn>
          <WordReveal
            text="50 assets. 20 videos. All in 2 months."
            className="section-title"
            style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1.15, display: 'block', textAlign: 'center', marginBottom: 56 }}
          />

          <StaggerContainer className="grid-4" staggerDelay={0.1}>
            {deliverables.map((d, i) => (
              <StaggerItem key={i}>
                <motion.div style={styles.delCard} whileHover={{ borderColor: 'rgba(62,117,98,0.2)', y: -4 }}>
                  <RevealImage
                    src={d.image}
                    alt={d.label}
                    style={{ width: '100%', height: 160 }}
                  />
                  <div style={{ padding: '20px 20px 24px' }}>
                    <span style={styles.delNum}>{d.num}</span>
                    <h4 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: 8 }}>{d.label}</h4>
                    <p style={{ fontSize: '0.8rem', color: '#777', lineHeight: 1.6 }}>{d.desc}</p>
                  </div>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>

          <HexDivider />

          <FadeIn style={{ textAlign: 'center' }}>
            <div style={styles.ctaBlock}>
              <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 800, marginBottom: 16, letterSpacing: '-1px' }}>
                Ready to achieve results like these?
              </h2>
              <p style={{ color: '#888', marginBottom: 32 }}>Let's discuss your transformation.</p>
              <MagneticButton>
                <Link to="/contact" className="btn btn-primary">
                  Book a Discovery Call <HiArrowRight />
                </Link>
              </MagneticButton>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

const styles = {
  heroSection: {
    minHeight: '65vh',
    display: 'flex',
    alignItems: 'center',
    paddingTop: 180,
    paddingBottom: 80,
    position: 'relative',
    overflow: 'hidden',
  },
  heroBg: {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
  },
  heroOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(11,11,11,0.85) 0%, rgba(11,11,11,0.7) 50%, rgba(11,11,11,0.92) 100%)',
  },
  clientCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 32, padding: 40, background: 'rgba(17,17,18,0.8)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.04)', marginTop: 40 },
  clientTag: { padding: '5px 14px', border: '1px solid rgba(62,117,98,0.15)', color: '#777', fontSize: '0.72rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 },
  badge: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '16px 24px', border: '1px solid rgba(62,117,98,0.15)' },
  resultCard: { padding: 36, border: '1px solid rgba(255,255,255,0.04)', background: '#161618', textAlign: 'center', transition: '0.4s' },
  resultLabel: { fontSize: '0.85rem', color: '#777', fontWeight: 500, marginTop: 8 },
  delCard: { border: '1px solid rgba(255,255,255,0.04)', background: '#111112', textAlign: 'center', transition: '0.4s', overflow: 'hidden' },
  delNum: { fontSize: '2rem', fontWeight: 800, color: '#3E7562', display: 'block', margin: '0 0 4px' },
  ctaBlock: { padding: '80px 48px', background: '#111112', border: '1px solid rgba(62,117,98,0.08)', textAlign: 'center' },
};
